/**
 * Helpers module
 * @module helpers
 */

import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';

// Predefined sanitization configurations for better performance and maintainability
const SANITIZATION_PRESETS = {
    STRICT: {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: []
    },
    BASIC: {
        ALLOWED_TAGS: [
            'b',
            'i',
            'strong',
            'em',
            'span',
            'sub',
            'sup',
            'br',
            'p',
            'u',
            's',
            'ol',
            'ul',
            'li',
            'p'
        ],
        ALLOWED_ATTR: ['style']
    },
    PERMISSIVE: {
        ALLOWED_TAGS: [
            'b',
            'i',
            'strong',
            'em',
            'span',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'p',
            'ul',
            'ol',
            'li',
            'br',
            'a'
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ADD_ATTR: ['target', 'rel']
    }
};

export const loading = { loading: true };

// Create a single textarea element for decoding HTML entities
const decoderElement = document.createElement('textarea');

/**
 * Decodes HTML entities in a string using a cached textarea element.
 * @param {string} text - The input string containing HTML entities.
 * @returns {string} - The decoded string.
 */
function decodeHTMLEntities(text) {
    decoderElement.innerHTML = text;
    const result = decoderElement.value;
    decoderElement.innerHTML = ''; // Clear for safety
    return result;
}

/**
 * Sanitizes HTML input to prevent XSS and removes malicious tags/attributes
 * while preserving the order of attributes.
 * We need to preserve the attributes order because we check with regex
 * in some places like <span class="data-box" data-no-html="true">
 *
 * @param htmlContent - The input string that may contain HTML.
 * @returns sanitized HTML string
 */
export function sanitizeEncodedHtml(htmlContent) {
    if (!htmlContent) return '';

    // decode encoded HTML like &lt; &gt; &amp;
    const decodedHtml = decodeHTMLEntities(htmlContent);

    // Add a hook to preserve attribute order
    DOMPurify.addHook('beforeSanitizeAttributes', node => {
        // Store the original attribute names to maintain order
        if (node && node.attributes) {
            node._originalAttrNames = Array.from(node.attributes).map(
                attr => attr.name
            );
        }
    });

    DOMPurify.addHook('afterSanitizeAttributes', node => {
        // Restore allowed attributes in original order
        if (
            node &&
            node._originalAttrNames &&
            Array.isArray(node._originalAttrNames)
        ) {
            node._originalAttrNames.forEach(name => {
                if (node.hasAttribute && node.hasAttribute(name)) {
                    const value = node.getAttribute(name);
                    node.removeAttribute(name);
                    node.setAttribute(name, value);
                }
            });
            delete node._originalAttrNames;
        }
    });

    // Sanitize the HTML
    const sanitizedHtml = DOMPurify.sanitize(decodedHtml);

    // Remove the hooks after sanitization
    DOMPurify.removeHook('beforeSanitizeAttributes');
    DOMPurify.removeHook('afterSanitizeAttributes');

    // Re-encode specific tags after sanitization
    const tagsToEncode = [
        'b',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'code',
        'pre',
        'a'
    ];

    // Build regex to match opening, closing, and self-closing tags (with or without attributes)
    const tagPattern = tagsToEncode.join('|');
    // Matches: <tag>, <tag attr="value">, </tag>, or <tag />
    // Note: This regex is safe to use because it runs on DOMPurify's sanitized output,
    // where any '>' characters inside attribute values are already encoded as '&gt;'
    const tagRegex = new RegExp(
        `<(\/?)(${tagPattern})(\\s[^>]*)?(\\/?)>`,
        'gi'
    );

    // Replace matched tags with encoded version (encode only the opening < and closing >)
    const encodedHtml = sanitizedHtml.replace(
        tagRegex,
        match => '&lt;' + match.slice(1, -1) + '&gt;'
    );

    return encodedHtml;
}

export const stripHtml = html => {
    const divEl = document.createElement('div');
    divEl.innerHTML = html;
    return divEl.textContent || divEl.innerText || '';
};

// looking for is the presence of markup, rather than simply text
const tagsRE = /<\/?[\D!][\s\S]*>/;
export const haveMarkup = text => tagsRE.test(text);

/*
note: stripHtml use a div as wrapper,
and it is not guaranteed to be always a valid parent node
*/
export const markup2text = (markup, strip = 'style,script,link') => {
    const fragBody = document
        .createDocumentFragment()
        .appendChild(document.createElement('body'));
    fragBody.innerHTML = markup;
    if (strip?.length) {
        fragBody.querySelectorAll(strip)?.forEach(e => e.remove());
    }
    return fragBody.textContent || fragBody.innerText;
};

export const toSanitizedMarkup = text => {
    let str = text || '';
    if (str) {
        str = text.trim();
        if (str) {
            str = stripHtml(str);
            if (str) {
                str = str.trim();
                if (str) {
                    str = DOMPurify.sanitize(str);
                }
            }
        }
    }
    return str;
};

export const getInnerHeight = node => {
    if (node) {
        const computedStyle = getComputedStyle(node);
        return (
            node.clientHeight -
            (parseInt(computedStyle.paddingTop) +
                parseInt(computedStyle.paddingBottom))
        );
    }
    return 0;
};

// Re-export from dedicated module for backwards compatibility
export { checkFeatureFlag, invalidateFeatureFlagCache } from './featureFlags';

/**
 * Check the value before update
 * @param value
 * @param initialValue
 * @returns {*}
 * @author FM
 */
export const usePrevious = (value, initialValue) => {
    const ref = useRef(initialValue);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

/**
 * Use instead of use effect to debug dependency mutation
 * @param effectHook
 * @param dependencies
 * @param dependencyNames
 * @author FM
 */
export const useEffectDebugger = (
    effectHook,
    dependencies,
    dependencyNames = []
) => {
    const previousDeps = usePrevious(dependencies, []);

    const changedDeps = dependencies.reduce((accum, dependency, index) => {
        if (dependency !== previousDeps[index]) {
            const keyName = dependencyNames[index] || index;
            return {
                ...accum,
                [keyName]: {
                    before: previousDeps[index],
                    after: dependency
                }
            };
        }

        return accum;
    }, {});

    if (Object.keys(changedDeps).length) {
        console.log('[use-effect-debugger] ', changedDeps);
    }

    useEffect(effectHook, dependencies);
};

/**
 * Sanitizes HTML content using DOMPurify with configurable presets
 * @param {string} html - The HTML string to sanitize
 * @param {Object} options - Configuration options
 * @param {string} options.preset - Sanitization preset: 'BASIC', 'STRICT', 'PERMISSIVE', 'CUSTOM'
 * @param {Object} options.custom - Custom configuration object when using 'CUSTOM' preset
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtmlContentWithOptions(
    html,
    { preset = 'BASIC', custom = {} } = {}
) {
    // Handle null, undefined, or non-string inputs
    if (typeof html !== 'string') {
        return '';
    }

    // Use predefined preset or custom configuration
    const config =
        preset === 'CUSTOM'
            ? custom
            : SANITIZATION_PRESETS[preset] || SANITIZATION_PRESETS.STRICT;

    return DOMPurify.sanitize(html, config);
}
